import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { roleService } from "@/services/roleService";
import type { RoleCreatePayload, RoleUpdatePayload } from "@/types/role";

const keys = {
  roles: (wsId: number) => ["roles", wsId] as const,
  permissions: (wsId: number) => ["permissions", wsId] as const,
};

export function useRoles(workspaceId: number | null) {
  return useQuery({
    queryKey: keys.roles(workspaceId ?? 0),
    queryFn: () => roleService.list(workspaceId as number),
    enabled: workspaceId != null,
  });
}

export function usePermissions(workspaceId: number | null) {
  return useQuery({
    queryKey: keys.permissions(workspaceId ?? 0),
    queryFn: () => roleService.listPermissions(workspaceId as number),
    enabled: workspaceId != null,
  });
}

export function useCreateRole(workspaceId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: RoleCreatePayload) => roleService.create(workspaceId, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.roles(workspaceId) }),
  });
}

export function useUpdateRole(workspaceId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roleId, payload }: { roleId: number; payload: RoleUpdatePayload }) =>
      roleService.update(workspaceId, roleId, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.roles(workspaceId) }),
  });
}

export function useDeleteRole(workspaceId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (roleId: number) => roleService.remove(workspaceId, roleId),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.roles(workspaceId) }),
  });
}
